import type { JobLog, JobLogCreateRequest } from '@techgitter/contracts';

const API_BASE_URL = '/api';

/** Which parent entity a log binds to. Jobs use /api/jobs/{guid}/logs;
 *  standalone appointments use /api/appointments/{guid}/logs. */
export type JobLogParent = 'job' | 'appointment';

function logsPath(guid: string, parent: JobLogParent): string {
  const collection = parent === 'appointment' ? 'appointments' : 'jobs';
  return `${API_BASE_URL}/${collection}/${encodeURIComponent(guid)}/logs`;
}

export async function getJobLogs(
  guid: string,
  signal?: AbortSignal,
  parent: JobLogParent = 'job',
): Promise<JobLog[]> {
  const response = await fetch(logsPath(guid, parent), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch job logs: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return Array.isArray(data) ? data : (data.items ?? []);
}

export async function createJobLog(
  guid: string,
  req: Omit<JobLogCreateRequest, 'jobGUID'>,
  signal?: AbortSignal,
  parent: JobLogParent = 'job',
): Promise<JobLog> {
  const response = await fetch(logsPath(guid, parent), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to create job log: ${response.status} ${response.statusText}`);
  }

  return response.json();
}
